import { DestroyRef, Injectable, signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { catchError, of, debounceTime, distinctUntilChanged, filter, Subject } from 'rxjs';
import type {
  FilterResponse,
  AppliedFilters,
  Counterpart,
  CounterpartsResponse,
  IFilterStore,
} from '../models';
import { TransactionFilterApi, TransactionApi } from '../apis';
import { RouteNavigator } from '../services/route-navigator.service';
import { FilterUrlService, NotificationService } from '../services';
import { extractErrorMessage } from '@shared/utils/http-error.util';

/**
 * TransactionFilterStore
 * Centralized state management for transaction filters using Angular Signals
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionFilterStore implements IFilterStore {
  private readonly filterApi = inject(TransactionFilterApi);
  private readonly transactionApi = inject(TransactionApi);
  private readonly filterUrlService = inject(FilterUrlService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly routeNavigator = inject(RouteNavigator);
  private readonly destroyRef = inject(DestroyRef);

  // State signals
  private readonly _availableFilters = signal<FilterResponse | null>(null);
  private readonly _appliedFilters = signal<AppliedFilters>({});
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _counterparts = signal<Counterpart[]>([]);

  readonly counterparts = this._counterparts.asReadonly();

  /**
   * Event stream — fires only when the user explicitly applies, clears, or resets filters.
   * Consumers (e.g. TransactionListComponent) subscribe here to reload data.
   * Intentionally separate from the appliedFilters state signal:
   * - appliedFilters answers "what are the current filters?"
   * - filterApplied$ answers "did the user just change a filter?"
   * This distinction eliminates the need for skip(1) workarounds in consumers.
   */
  private readonly _filterApplied$ = new Subject<AppliedFilters>();
  readonly filterApplied$ = this._filterApplied$.asObservable();

  /**
   * Emits when browser Back/Forward navigation changes the active filters.
   *
   * Distinct from filterApplied$:
   * - filterApplied$     -> user explicitly applied/cleared a filter in the UI
   * - navFiltersChanged$ -> the URL changed externally (back/forward) and the
   *                         decoded params differ from the current store state
   *
   * Consumers (e.g. TransactionListComponent) use this to sync their local
   * chip-bar signal and trigger a data reload without causing double-reloads
   * on regular user-driven filter interactions.
   */
  private readonly _navFiltersChanged$ = new Subject<AppliedFilters>();
  readonly navFiltersChanged$ = this._navFiltersChanged$.asObservable();

  constructor() {
    // Eagerly parse URL params synchronously on store construction.
    // This runs before NavigationEnd fires (which happens after component ngOnInit),
    // so components that read appliedFilters() in ngOnInit get the correct initial
    // filters.
    const initialParams = this.router.parseUrl(location.search || '/').queryParams;
    const initialFilters = this.filterUrlService.decodeParamsToFilters(initialParams);
    if (Object.keys(initialFilters).length > 0) {
      this._appliedFilters.set(initialFilters);
    }

    // Watch router NavigationEnd events to sync URL params to appliedFilters signal.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        debounceTime(50),
        distinctUntilChanged((a, b) => a.urlAfterRedirects === b.urlAfterRedirects),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const params = this.router.parseUrl(this.router.url).queryParams;
        const filters = this.filterUrlService.decodeParamsToFilters(params);
        if (!this.areFiltersEqual(this._appliedFilters(), filters)) {
          this._appliedFilters.set(filters);
          // Notify consumers that back/forward navigation changed the filters.
          // areFiltersEqual being false here guarantees this is a genuine URL
          // change — not a redundant NavigationEnd from a chip-bar filter apply.
          this._navFiltersChanged$.next(filters);
        }
      });
  }

  // Public readonly signals
  readonly availableFilters = this._availableFilters.asReadonly();
  readonly appliedFilters = this._appliedFilters.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly activeFilterCount = computed<number>(() => {
    const filters = this._appliedFilters();
    let count = 0;
    if (filters.accountIds?.length) count++;
    if (filters.counterpartIds?.length) count++;
    if (filters.status?.length) count++;
    if (filters.transactionTypes?.length) count++;
    if (filters.categoryIds?.length) count++;
    if (filters.transactionDirection?.length) count++;
    if (filters.dateRange?.preset || filters.dateRange?.startDate || filters.dateRange?.endDate) {
      count++;
    }
    return count;
  });

  readonly hasActiveFilters = computed<boolean>(() => {
    return this.activeFilterCount() > 0;
  });

  readonly hasAdvancedFilters = computed<boolean>(() => {
    const filters = this._appliedFilters();
    return !!(
      filters.transactionTypes?.length ||
      filters.categoryIds?.length ||
      filters.counterpartIds?.length ||
      filters.accountIds?.length
    );
  });

  loadFilterOptions(): void {
    this._loading.set(true);
    this._error.set(null);

    this.filterApi
      .getFilters()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          const message = extractErrorMessage(error, 'Echec du chargement des options de filtrage');
          this.notificationService.error(message, 'Erreur de chargement');
          this._error.set(message);
          this._loading.set(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response) {
          this._availableFilters.set(response);
        }
        this._loading.set(false);
      });
  }

  applyFilters(filters: Partial<AppliedFilters>): void {
    const currentFilters = this._appliedFilters();
    const mergedFilters: AppliedFilters = { ...currentFilters, ...filters };
    const sanitizedFilters = this.sanitizeFilters(mergedFilters);

    this._appliedFilters.set(sanitizedFilters);

    const params = this.filterUrlService.encodeFiltersToParams(sanitizedFilters);
    this.updateQueryParams({ ...this.filterUrlService.getEmptyParams(), ...params });

    this._filterApplied$.next(sanitizedFilters);
  }

  replaceFilters(filters: AppliedFilters): void {
    const sanitizedFilters = this.sanitizeFilters(filters);
    this._appliedFilters.set(sanitizedFilters);
    const params = this.filterUrlService.encodeFiltersToParams(sanitizedFilters);
    this.updateQueryParams({ ...this.filterUrlService.getEmptyParams(), ...params });
    this._filterApplied$.next(sanitizedFilters);
  }

  clearFilter(filterKey: keyof AppliedFilters): void {
    const currentFilters = { ...this._appliedFilters() };
    if (filterKey === 'searchTerm') {
      delete currentFilters['searchMode'];
    }
    delete currentFilters[filterKey];

    this._appliedFilters.set(currentFilters);

    const params = this.filterUrlService.encodeFiltersToParams(currentFilters);
    this.updateQueryParams({ ...this.filterUrlService.getEmptyParams(), ...params });

    this._filterApplied$.next(currentFilters);
  }

  clearAllFilters(): void {
    this._appliedFilters.set({});
    this.updateQueryParams(this.filterUrlService.getEmptyParams());
    this._filterApplied$.next({});
  }

  private sanitizeFilters(filters: AppliedFilters): AppliedFilters {
    const sanitized: AppliedFilters = {};
    if (filters.accountIds?.length) sanitized.accountIds = filters.accountIds;
    if (filters.status?.length) sanitized.status = filters.status;
    if (filters.transactionTypes?.length) sanitized.transactionTypes = filters.transactionTypes;
    if (filters.categoryIds?.length) sanitized.categoryIds = filters.categoryIds;
    if (filters.transactionDirection?.length)
      sanitized.transactionDirection = filters.transactionDirection;
    if (filters.counterpartIds?.length) sanitized.counterpartIds = filters.counterpartIds;
    if (filters.searchTerm) {
      const trimmed = filters.searchTerm.trim();
      if (trimmed) {
        sanitized.searchTerm = trimmed.substring(0, 100);
        if (filters.searchMode) sanitized.searchMode = filters.searchMode;
      }
    }
    if (filters.dateRange) {
      const { preset, startDate, endDate } = filters.dateRange;
      if (preset || startDate || endDate) {
        sanitized.dateRange = {};
        if (preset) sanitized.dateRange.preset = preset;
        if (startDate?.trim()) sanitized.dateRange.startDate = startDate.trim();
        if (endDate?.trim()) sanitized.dateRange.endDate = endDate.trim();
      }
    }
    return sanitized;
  }

  loadCounterparts(): void {
    if (this._counterparts().length > 0) return;
    this.transactionApi
      .getCounterparts()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          const message = extractErrorMessage(error, 'Echec du chargement des clients');
          this.notificationService.error(message, 'Erreur de chargement');
          return of(null as CounterpartsResponse | null);
        }),
      )
      .subscribe((response) => {
        if (response) {
          this._counterparts.set(response.counterparts);
        }
      });
  }

  private updateQueryParams(params: Record<string, string | null>): void {
    const urlTree = this.router.parseUrl(this.router.url);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null) {
        delete urlTree.queryParams[key];
      } else {
        urlTree.queryParams[key] = value;
      }
    });
    this.routeNavigator.toUrl(urlTree);
  }

  private areFiltersEqual(a: AppliedFilters, b: AppliedFilters): boolean {
    const arrayPropsEqual =
      this.arraysEqual(a.accountIds, b.accountIds) &&
      this.arraysEqual(a.status, b.status) &&
      this.arraysEqual(a.transactionTypes, b.transactionTypes) &&
      this.arraysEqual(a.categoryIds, b.categoryIds) &&
      this.arraysEqual(a.transactionDirection, b.transactionDirection) &&
      this.arraysEqual(a.counterpartIds, b.counterpartIds);
    const dateRangeEqual =
      a.dateRange?.preset === b.dateRange?.preset &&
      a.dateRange?.startDate === b.dateRange?.startDate &&
      a.dateRange?.endDate === b.dateRange?.endDate;
    const searchTermEqual = a.searchTerm === b.searchTerm && a.searchMode === b.searchMode;
    return arrayPropsEqual && dateRangeEqual && searchTermEqual;
  }

  private arraysEqual(
    a: string[] | number[] | undefined,
    b: string[] | number[] | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }
}
